import React, { useCallback, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    number, object, string, ObjectSchema,
} from 'yup';
import { noop } from 'lodash/fp';

import { OPTION_LIST, switzLandCountryId } from '../../constants/constants';
import { useOptionList } from '../../hooks/useOptionList';
import { useYupValidationResolver } from '../../hooks/useYupValidationResolver';
import { FIELD_LENGTH } from '../../pages/ClientOverview/constants';
import {
    Button,
    Column,
    FormGroup,
    Icon,
    Infobox,
    Loader,
    Modal,
    notification,
    Row,
    Select,
    TextInput,
} from '../../ui-library';
import {
    ICON_TYPE_DELETE, ICON_TYPE_EDIT, ICON_TYPE_PLUS_BUTTON, ICON_TYPE_CLOSE_SLIM,
} from '../../ui-library/components/Icon';
import { checkNullData } from '../../utils';
import { validateMultipleInputsLength } from '../../validation/validation';
import Preloader from '../Preloader';
import './MailingAddress.css';

// 1. Define the Data Shape
export interface MailingAddressData {
    mailRecipientGender?: number;
    mailRecipientDateOfBirth?: string;
    mailRecipientAddressLine1?: string | null;
    mailRecipientLanguage?: number;
    mailRecipientAddressLine5?: string | null;
    mailRecipientFirstName?: string | null;
    mailRecipientLastName?: string | null;
    mailRecipientAddressLine2?: string | null;
    mailRecipientAddressLine3?: string | null;
    mailRecipientAddressLine4?: string | number | null;
    mailRecipientAddressCountry?: number;
}

interface MailingAddressProps {
    initData?: MailingAddressData | Record<string, any>;
    onSubmit?: (data: Partial<MailingAddressData>, mode?: number) => Promise<void> | void;
    required?: boolean;
    error?: boolean;
    isCHOnly?: boolean;
}

export const TARGET_FIELDS: (keyof MailingAddressData)[] = [
    'mailRecipientGender',
    'mailRecipientDateOfBirth',
    'mailRecipientAddressLine1',
    'mailRecipientLanguage',
    'mailRecipientAddressLine5',
    'mailRecipientFirstName',
    'mailRecipientLastName',
    'mailRecipientAddressLine2',
    'mailRecipientAddressLine3',
    'mailRecipientAddressLine4',
    'mailRecipientAddressCountry',
];

const MailingAddress: React.FC<MailingAddressProps> = ({
    initData = {},
    onSubmit = noop,
    required = false,
    error,
    isCHOnly = false,
}) => {
    const { t } = useTranslation();
    const [isVisible, setVisible] = useState(false);
    const [isDataSending, setDataSending] = useState(false);
    const [isDeleting, setDeleting] = useState(false);
    const [generalError, setGeneralError] = useState<string | null>();

    const onAdd = useCallback(() => setVisible(true), []);

    const {
        lists: [optionGender, optionCountry, optionLanguage],
        isLoading: isOptionsLoading,
    } = useOptionList([OPTION_LIST.gender, OPTION_LIST.country, OPTION_LIST.language]);

    // Helpers to adapt options
    const adaptOptions = (list: any[] | undefined) => list?.map(({ id, label }) => ({ value: id, label })) ?? [];

    const optionGenderAdapted = useMemo(() => adaptOptions(optionGender), [optionGender]);
    const optionLanguageAdapted = useMemo(() => adaptOptions(optionLanguage), [optionLanguage]);
    const optionCountryAdapted = useMemo(() => (
        optionCountry?.map(({
            id,
            label,
        }) => ({
            value: id,
            label,
        }))?.filter(({ value }) => (isCHOnly ? value === switzLandCountryId : true)) ?? []
    ), [optionCountry, isCHOnly]);

    const initDataAdapted = useMemo(() => (
        TARGET_FIELDS.reduce((accu, fieldName) => {
            accu[fieldName] = (initData as any)?.[fieldName];

            return accu;
        }, {} as MailingAddressData)
    ), [initData]);

    const isDataAvailable = useMemo(() => (
        !!TARGET_FIELDS.find((fieldName) => !checkNullData((initData as any)?.[fieldName]))
    ), [initData]);

    const schema: ObjectSchema<any> = useMemo(() => object().shape({
        mailRecipientGender: number().typeError(t('validation.mandatoryField')).required(t('validation.mandatoryField')),
        mailRecipientAddressCountry: number().typeError(t('validation.mandatoryField')).required(t('validation.mandatoryField')),
        mailRecipientLanguage: number().typeError(t('validation.mandatoryField')).required(t('validation.mandatoryField')),
        mailRecipientFirstName: string().nullable().max(FIELD_LENGTH.firstName, t('onboardingFlow.personalPage.validateInputLength', { length: FIELD_LENGTH.firstName })).required(t('validation.mandatoryField')),
        mailRecipientLastName: string().nullable().max(FIELD_LENGTH.lastName, t('onboardingFlow.personalPage.validateInputLength', { length: FIELD_LENGTH.lastName })).required(t('validation.mandatoryField')),
        mailRecipientAddressLine1: string().nullable().max(FIELD_LENGTH.street, t('onboardingFlow.personalPage.validateInputLength', { length: FIELD_LENGTH.street })),
        mailRecipientAddressLine2: string().nullable(),
        mailRecipientAddressLine3: string().nullable().max(FIELD_LENGTH.street, t('onboardingFlow.personalPage.validateInputLength', { length: FIELD_LENGTH.street })).required(t('validation.mandatoryField')),
        mailRecipientAddressLine4: number().typeError(t('onboardingFlow.personalPage.validationNumber')).required(t('validation.mandatoryField')),
        mailRecipientAddressLine5: string().nullable().required(t('validation.mandatoryField')).max(FIELD_LENGTH.city, t('onboardingFlow.personalPage.validateInputLength', { length: FIELD_LENGTH.city })),
    }), [t]);

    const resolver = useYupValidationResolver(schema);

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
        register,
        reset,
        setError,
    } = useForm<MailingAddressData>({
        resolver,
        values: initDataAdapted,
    });

    const formData = watch();

    const onOk = useCallback(async () => {
        try {
            setDataSending(true);
            const isLengthValid = validateMultipleInputsLength(
                FIELD_LENGTH.firstAndLastNameCombined,
                'clientValidation.firstAndLastNameLengthCombined',
                formData.mailRecipientFirstName || '',
                formData.mailRecipientLastName || '',
            );

            if (isLengthValid) {
                setGeneralError(t(isLengthValid, { max: FIELD_LENGTH.firstAndLastNameCombined }));
                setError('mailRecipientFirstName', { message: ' ' });
                setError('mailRecipientLastName', { message: ' ' });

                return;
            }

            setGeneralError(null);
            setError('mailRecipientFirstName', { message: '' });
            setError('mailRecipientLastName', { message: '' });

            await onSubmit(formData);
            setVisible(false);
            reset(formData);
        } catch (e) {
            setGeneralError(t('advisoryDashboard.error'));
        } finally {
            setDataSending(false);
        }
    }, [formData, t, setError, onSubmit, reset]);

    const deleteMailAddress = useCallback(async () => {
        try {
            setDeleting(true);
            await onSubmit({}, 1);
            reset({});
        } catch (e) {
            notification.open({
                content: t('MailingAddress.deleteMailingAddress.error'),
                type: 'error',
            });
        } finally {
            setDeleting(false);
        }
    }, [onSubmit, reset, t]);

    const handleDelete = useCallback(() => {
        Modal.confirm({
            title: t('MailingAddress.confirm.delete.title'),
            content: t('MailingAddress.confirm.delete.content'),
            okText: t('confirmation.confirm'),
            onOk: deleteMailAddress,
            cancelText: t('confirmation.cancel'),
        });
    }, [t, deleteMailAddress]);

    const onCancel = useCallback(() => {
        setGeneralError(undefined);
        setVisible(false);
    }, []);

    const presentationalDataElement = useMemo(() => {
        if (!isDataAvailable) return null;

        const countryLabel = optionCountry?.find(({ id }) => initDataAdapted.mailRecipientAddressCountry === id)?.label ?? '';

        return (
            <p>
                {`${initDataAdapted?.mailRecipientAddressLine3 ?? ''}, ${initDataAdapted?.mailRecipientAddressLine5 ?? ''}, ${countryLabel}`}
                {' '}
                {isDeleting ? <Loader /> : (
                    <Button type="link" onClick={handleDelete}>
                        <Icon type={ICON_TYPE_DELETE} />
                    </Button>
                )}
            </p>
        );
    }, [isDataAvailable, isDeleting, initDataAdapted, optionCountry, handleDelete]);

    const closeIcon = useMemo(() => (<Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />), []);

    return (
        <div className="MailingAddress">
            <FormGroup underline={false}>
                <legend>
                    {required && (
                        <Infobox warning showIcon={false} className="mb-16">
                            {t('clientDetails.mailingAddress.juniorMailAddressRequiredMessage')}
                        </Infobox>
                    )}
                    <span className={error ? 'error' : ''}>
                        {t('MailingAddress.button.mailingAddress')}
                        {required && <span>&nbsp;*</span>}
                    </span>
                    <Button type="link" onClick={onAdd} className="action-button">
                        <Icon type={isDataAvailable ? ICON_TYPE_EDIT : ICON_TYPE_PLUS_BUTTON} />
                    </Button>
                </legend>
                {presentationalDataElement}
                {isVisible && (
                    <Modal
                        className="MailingAddress__MailingAddressDialog"
                        title={t('dynamicCRM.mailRecipientFieldset')}
                        okText={t('confirmation.confirm')}
                        cancelText={t('confirmation.cancel')}
                        footer={(
                            <div>
                                <Button type="secondary" onClick={onCancel}>
                                    {t('clientDetails.mailingAddress.Cancel')}
                                </Button>
                                <Button
                                    type="primary"
                                    disabled={isDataSending || isOptionsLoading}
                                    onClick={handleSubmit(onOk)}
                                >
                                    {isDataSending ? <Loader /> : t('clientDetails.mailingAddress.Submit')}
                                </Button>
                            </div>
                        )}
                        width="80%"
                        visible={isVisible}
                        onOk={handleSubmit(onOk)}
                        onCancel={onCancel}
                        keyboard={false}
                        maskClosable={false}
                        closable
                        borderRadius
                        closeIcon={closeIcon}
                    >
                        <Preloader isLoading={isOptionsLoading}>
                            <Row>
                                <Column size="6">
                                    <TextInput
                                        {...register('mailRecipientFirstName')}
                                        data-cy="mailRecipientFirstName"
                                        label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientFirstName')}
                                        error={errors?.mailRecipientFirstName?.message}
                                        required
                                    />
                                </Column>
                                <Column size="6">
                                    <TextInput
                                        {...register('mailRecipientLastName')}
                                        data-cy="mailRecipientLastName"
                                        label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientLastName')}
                                        error={errors?.mailRecipientLastName?.message}
                                        required
                                    />
                                </Column>
                            </Row>
                            <Row>
                                <Column size="6">
                                    <Controller
                                        name="mailRecipientGender"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                placeholder={t('portfolio.createNewPortfolio.agentInformation.selectPlaceholder')}
                                                label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientGender')}
                                                options={optionGenderAdapted}
                                                error={errors?.mailRecipientGender?.message}
                                                required
                                            />
                                        )}
                                    />
                                </Column>
                                <Column size="6">
                                    <Controller
                                        name="mailRecipientLanguage"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                placeholder={t('portfolio.createNewPortfolio.agentInformation.selectPlaceholder')}
                                                label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientLanguage')}
                                                options={optionLanguageAdapted}
                                                error={errors?.mailRecipientLanguage?.message}
                                                required
                                            />
                                        )}
                                    />
                                </Column>
                            </Row>
                            <Row>
                                <Column size="6">
                                    <TextInput
                                        {...register('mailRecipientAddressLine1')}
                                        data-cy="mailRecipientAddressLine1"
                                        label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientAddressLine1')}
                                        error={errors?.mailRecipientAddressLine1?.message}
                                    />
                                </Column>
                                <Column size="6">
                                    <TextInput
                                        {...register('mailRecipientAddressLine2')}
                                        data-cy="mailRecipientAddressLine2"
                                        label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientAddressLine2')}
                                        error={errors?.mailRecipientAddressLine2?.message}
                                    />
                                </Column>
                            </Row>
                            <Row>
                                <Column size="6">
                                    <TextInput
                                        {...register('mailRecipientAddressLine3')}
                                        data-cy="mailRecipientAddressLine3"
                                        label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientAddressLine3')}
                                        error={errors?.mailRecipientAddressLine3?.message}
                                        required
                                    />
                                </Column>
                                <Column size="6">
                                    <TextInput
                                        {...register('mailRecipientAddressLine4')}
                                        data-cy="mailRecipientAddressLine4"
                                        label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientAddressLine4')}
                                        error={errors?.mailRecipientAddressLine4?.message}
                                        required
                                    />
                                </Column>
                            </Row>
                            <Row>
                                <Column size="6">
                                    <TextInput
                                        {...register('mailRecipientAddressLine5')}
                                        data-cy="mailRecipientAddressLine5"
                                        label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientAddressLine5')}
                                        error={errors?.mailRecipientAddressLine5?.message}
                                        required
                                    />
                                </Column>
                                <Column size="6">
                                    <Controller
                                        name="mailRecipientAddressCountry"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                placeholder={t('portfolio.createNewPortfolio.agentInformation.selectPlaceholder')}
                                                label={t('dynamicCRM.communicationMethods.mailRecipient.mailRecipientAddressCountry')}
                                                options={optionCountryAdapted}
                                                error={errors?.mailRecipientAddressCountry?.message}
                                                required
                                            />
                                        )}
                                    />
                                </Column>
                            </Row>
                            {generalError && <Infobox error>{generalError}</Infobox>}
                        </Preloader>
                    </Modal>
                )}
            </FormGroup>
        </div>
    );
};

export default MailingAddress;
